import { getDataTableByCanonicalType } from '../../features/visual-analytics/charts/chartDataBindings';
import { sendEvent } from '../../features/visual-analytics/hooks/events';
import { TpCanonicalTypeInfo, TpDataContext } from '../interfaces';
import { TpActiveEnSelectionFilter, TpEnSelectionFilterStep, TpEnSelectionFilterTypes } from './interfaces';
import { defEnSelectionFilterCategorical } from './selectionEnFilterCategorical';
import { defEnSelectionFilterDualCategorical } from './selectionEnFilterDualCategorical';
import { defEnSelectionFilterIntersections } from './selectionEnFilterIntersections';
import { defEnSelectionFilterLasso2D } from './selectionEnFilterLasso2D';
import { defEnSelectionFilterManual } from './selectionEnFilterManual';
import { defEnSelectionFilterMultiLevelCat } from './selectionEnFilterMultiLevelCat';
import { defEnSelectionFilterNumericalRange } from './selectionEnFilterNumericalRange';
import { defEnSelectionFilterImport } from './selectionEnFilterImport';
import { TpFilterSchemaDTO } from '../dashboard-template/schemas';

export const filterTypeIds = [
  TpEnSelectionFilterTypes.CATEGORICAL,
  TpEnSelectionFilterTypes.DUALCATEGORICAL,
  TpEnSelectionFilterTypes.INTERSECTIONS,
  TpEnSelectionFilterTypes.NUMERICALRANGE,
  TpEnSelectionFilterTypes.LASSO2D,
  TpEnSelectionFilterTypes.MANUAL,
  TpEnSelectionFilterTypes.MULTILEVELCAT,
  TpEnSelectionFilterTypes.IMPORT,
];

export function getEnSelectionFilterDef(type: TpEnSelectionFilterTypes) {
  if (type == TpEnSelectionFilterTypes.CATEGORICAL) return defEnSelectionFilterCategorical;
  if (type == TpEnSelectionFilterTypes.DUALCATEGORICAL) return defEnSelectionFilterDualCategorical;
  if (type == TpEnSelectionFilterTypes.INTERSECTIONS) return defEnSelectionFilterIntersections;
  if (type == TpEnSelectionFilterTypes.NUMERICALRANGE) return defEnSelectionFilterNumericalRange;
  if (type == TpEnSelectionFilterTypes.LASSO2D) return defEnSelectionFilterLasso2D;
  if (type == TpEnSelectionFilterTypes.MANUAL) return defEnSelectionFilterManual;
  if (type == TpEnSelectionFilterTypes.MULTILEVELCAT) return defEnSelectionFilterMultiLevelCat;
  if (type == TpEnSelectionFilterTypes.IMPORT) return defEnSelectionFilterImport;
  throw `Unknown selection filter type ${type}`;
}

export function parseEnSelectionFilter(
  dataContext: TpDataContext,
  dataTableId: string,
  filterJson: TpFilterSchemaDTO,
): TpActiveEnSelectionFilter {
  return {
    steps: filterJson.steps.map((stepJson: any) =>
      getEnSelectionFilterDef(stepJson.type).fromJson!(dataContext, dataTableId, stepJson),
    ),
    manualSelection: false,
  };
}

export function createEmptyEnSelectionFilter(): TpActiveEnSelectionFilter {
  return {
    steps: [],
    manualSelection: false,
  };
}

export function isEmptyEnSelectionFilter(filter: TpActiveEnSelectionFilter): boolean {
  return filter.steps.length == 0 && !filter.manualSelection;
}

export function getEnSelectionFilterStep(
  selectionFilter: TpActiveEnSelectionFilter,
  key: string,
): TpEnSelectionFilterStep | null {
  for (const step of selectionFilter.steps) if (step.key == key) return step;
  return null;
}

export function removeEnSelectionFilterStep(selectionFilter: TpActiveEnSelectionFilter, key: string) {
  selectionFilter.steps = selectionFilter.steps.filter((item) => item.key != key);
}

function getIncludedCountForStep(canonicalTypeInfo: TpCanonicalTypeInfo, selectedItemIds: string[]) {
  if (!canonicalTypeInfo.restrictedItemsMap) return selectedItemIds.length;
  let count = 0;
  for (const itemId of selectedItemIds) if (canonicalTypeInfo.restrictedItemsMap[itemId]) count++;
  return count;
}

export function getSelectionItemsMapForFilter(
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
  selectionFilterSteps: TpEnSelectionFilterStep[],
) {
  const dataTable = getDataTableByCanonicalType(dataContext, canonicalTypeInfo.id);
  let selectedItemsMap: { [itemId: string]: boolean } = {};
  for (const itemId of dataTable.columnData[dataTable.colItemId]) selectedItemsMap[itemId] = true;

  for (const step of selectionFilterSteps) {
    const selectedItemIds = getEnSelectionFilterDef(step.type).performStep(dataContext, canonicalTypeInfo, step);
    step.includedCount = getIncludedCountForStep(canonicalTypeInfo, selectedItemIds);
    const nextMap: { [itemId: string]: boolean } = {};
    for (const itemId of selectedItemIds) if (selectedItemsMap[itemId]) nextMap[itemId] = true;
    selectedItemsMap = nextMap;
  }

  return selectedItemsMap;
}

export function calculateEnSelectionFilter(dataContext: TpDataContext, canonicalTypeInfo: TpCanonicalTypeInfo) {
  const selectionFilter = canonicalTypeInfo.selectionFilter;
  if (selectionFilter.steps.length == 0) {
    canonicalTypeInfo.selectedItemsMap = null;
    return;
  }
  canonicalTypeInfo.selectedItemsMap = getSelectionItemsMapForFilter(
    dataContext,
    canonicalTypeInfo,
    selectionFilter.steps,
  );
  selectionFilter.manualSelection = false;
}

export function smartRenderEnFilterItems(content: string | string[], forceString?: boolean) {
  if (!Array.isArray(content)) return content;
  if (content.length == 1) return content[0];
  if (content.length == 0) return '-';

  if (forceString) return content.join(', ');

  return (
    <ul>
      {content.map((item, idx) => (
        <li key={`item_${idx}`}>{item}</li>
      ))}
    </ul>
  );
}

export const MSGDataContextSelectionChanged = 'dataContextSelectionChanged';

export function notifyDataContextSelectionChanged(dataContextId: string, canonicalTypeId: string) {
  sendEvent(MSGDataContextSelectionChanged, {
    dataContextId,
    canonicalTypeId,
  });
}
